import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { PublicShell } from "@/components/PublicShell";
import {
  Target, Compass, Sparkles, ShieldCheck, UserPlus, BadgeCheck, Fingerprint,
  Wallet, Ticket, Store, Cpu, ReceiptText, Landmark, FileBarChart,
} from "lucide-react";

const leaders = [
  {
    id: "sukky",
    name: "Dr. Sukky Somkene Odabi",
    role: "Co-Founder & Chief Innovation, Strategy and Operations Officer",
    photo: "/leadership/sukky.png",
    bio: [
      "Dr. Sukky Somkene Odabi is an Innovation, Strategy and Digital Transformation Consultant and the Co-Founder of Text-Me-Food (TMF) Foundation, a technology-enabled social-impact initiative focused on making food and social benefits more accessible, dignified and measurable.",
      "He brings extensive experience in strategy development, technology innovation, institutional transformation and stakeholder engagement, with a track record of developing digital platforms and strategic programmes designed to improve public and social-impact delivery.",
      "Through his work with Service Management Consultancy Nigeria (SMCN) and various public-sector and development initiatives, Dr. Odabi has focused on applying technology and strategy to solve complex societal challenges and create scalable solutions.",
      "At TMF, he contributes to the development of the Foundation's digital beneficiary, voucher, sponsor and vendor ecosystem, helping to position TMF as a scalable infrastructure for sponsors, development organisations, philanthropists, governments and businesses to deliver measurable benefits to communities.",
    ],
    expertise: "Strategy • Innovation • Digital Transformation • Programme Development • Technology Solutions • Stakeholder Management • Social Impact",
  },
  {
    id: "jefferson",
    name: "Okoroafor Jefferson",
    role: "Co-Founder / Executive Director",
    bio: [
      "Okoroafor Jefferson is a Nigerian entrepreneur and social-impact strategist and the Co-Founder and Executive Director of Text Me Food (TMF), an innovative platform focused on improving access to food assistance through technology, digital identification, and structured distribution networks.",
      "Through TMF, Jefferson is working to develop a transparent and scalable system that connects beneficiaries, food vendors, sponsors, and institutional partners, enabling food support to reach intended recipients efficiently while providing accountability throughout the distribution process.",
      "With a strong interest in technology, grassroots development, entrepreneurship, and inclusive economic participation, Jefferson is committed to building practical solutions that address everyday challenges and create meaningful social and economic impact.",
      "As Executive Director, he provides strategic leadership across TMF's operations, partnerships, technology development, stakeholder engagement, and expansion strategy, with a vision of building TMF into a scalable platform for food assistance and social-impact programmes across Nigeria.",
    ],
  },
];

const vmp = [
  { icon: Target, t: "Vision", d: "To build Nigeria's most accessible technology-enabled food-support network, ensuring that vulnerable and supported Nigerians can access food where they live, while strengthening local food commerce." },
  { icon: Compass, t: "Mission", d: "To use technology, strategic partnerships and a nationwide network of stakeholders to make food accessible to beneficiaries through a secure, flexible and measurable digital food-value system." },
  { icon: Sparkles, t: "Core Principle", d: "TMF does not simply distribute food. TMF distributes purchasing power for food." },
];

const fraudSteps = [
  { t: "Register", icon: UserPlus }, { t: "Verify", icon: BadgeCheck },
  { t: "Create TMF ID", icon: Fingerprint }, { t: "Sponsor Allocates Benefit", icon: Wallet },
  { t: "Generate Claim Code", icon: Ticket }, { t: "Vendor Verifies", icon: Store },
  { t: "TMF Authorises", icon: Cpu }, { t: "Transaction Recorded", icon: ReceiptText },
  { t: "Settlement Reconciled", icon: Landmark }, { t: "Sponsor Report Generated", icon: FileBarChart },
];

export default function AboutUs() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } else window.scrollTo(0, 0);
  }, [location]);

  return (
    <PublicShell>
      <section className="bg-emerald-950 tmf-grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-20">
          <span className="text-xs font-mono uppercase tracking-widest text-emerald-300">About Us</span>
          <h1 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight max-w-3xl">
            Building trusted digital infrastructure for food access in Nigeria.
          </h1>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section id="leadership" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24 scroll-mt-24">
        <span className="text-xs font-mono uppercase tracking-widest text-emerald-700">Leadership & Governance</span>
        <h2 className="mt-3 font-display font-bold text-2xl sm:text-3xl text-emerald-950 tracking-tight">The people behind TMF</h2>
        <div className="mt-10 space-y-10">
          {leaders.map((l) => (
            <div key={l.id} data-testid={`leader-${l.id}`} className="grid lg:grid-cols-12 gap-8 items-start rounded-3xl border border-emerald-100 bg-white p-6 lg:p-8">
              {l.photo && (
                <div className="lg:col-span-4">
                  <div className="relative">
                    <div className="absolute -inset-3 bg-emerald-500/10 rounded-3xl blur-xl" />
                    <img src={l.photo} alt={l.name} className="relative rounded-2xl object-cover w-full h-80 border-4 border-white shadow-xl" />
                  </div>
                </div>
              )}
              <div className={l.photo ? "lg:col-span-8" : "lg:col-span-12"}>
                <h3 className="font-display font-bold text-2xl text-emerald-950">{l.name}</h3>
                <p className="mt-1 text-emerald-700 font-semibold text-sm">{l.role}</p>
                <div className="mt-4 space-y-3">
                  {l.bio.map((p, i) => <p key={`${l.id}-${i}`} className="text-gray-600 text-sm leading-relaxed">{p}</p>)}
                </div>
                {l.expertise && (
                  <p className="mt-4 text-xs text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3">
                    <span className="font-semibold">Core expertise:</span> {l.expertise}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* VISION MISSION PRINCIPLE */}
      <section id="vision" className="bg-emerald-50/60 border-y border-emerald-100 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24">
          <span className="text-xs font-mono uppercase tracking-widest text-emerald-700">Our Compass</span>
          <h2 className="mt-3 font-display font-bold text-2xl sm:text-3xl text-emerald-950 tracking-tight">Vision, Mission & Core Principle</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {vmp.map((v) => (
              <div key={v.t} data-testid={`vmp-${v.t.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-2xl bg-white border border-emerald-100 p-7">
                <div className="w-12 h-12 rounded-xl bg-emerald-600 flex items-center justify-center"><v.icon className="w-6 h-6 text-white" /></div>
                <h3 className="mt-5 font-display font-semibold text-xl text-emerald-950">{v.t}</h3>
                <p className="mt-3 text-gray-600 text-sm leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FRAUD PREVENTION */}
      <section id="fraud" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24 scroll-mt-24">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-7 h-7 text-emerald-600" />
          <span className="text-xs font-mono uppercase tracking-widest text-emerald-700">Trust & Accountability</span>
        </div>
        <h2 className="mt-3 font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-emerald-950 tracking-tight max-w-2xl">How TMF Prevents Fraud</h2>
        <p className="mt-4 text-gray-600 max-w-2xl">Every benefit flows through a verified, recorded and reconciled chain — no cash changes hands off-ledger, and every step is auditable end-to-end.</p>
        <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {fraudSteps.map((s, i) => (
            <div key={s.t} data-testid={`fraud-step-${i + 1}`} className="relative rounded-2xl border border-emerald-100 bg-white p-5 hover:shadow-lg hover:border-emerald-200 transition-all">
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center"><s.icon className="w-5 h-5 text-emerald-600" /></div>
                <span className="font-display font-extrabold text-emerald-100 text-xl">{String(i + 1).padStart(2, "0")}</span>
              </div>
              <div className="mt-3 font-semibold text-sm text-emerald-950">{s.t}</div>
            </div>
          ))}
        </div>
      </section>
    </PublicShell>
  );
}
